import { useMemo, useState, Fragment, type ReactNode } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  RocketLaunchIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { AppBadge, Badge, Btn, Card, cx } from '../../../components/admin/console';
import {
  useApps,
  useBuilds,
  usePointers,
  usePromoteBuild,
  usePromoteToReleaseChannel,
} from './data/hooks';
import { ReleaseNotesEditor } from './ReleaseNotesEditor';
import type { Build, ChannelPointer, OtaAppId, OtaReleaseChannelId } from './data/types';
import {
  OTA_RELEASE_CHANNEL_ORDER,
  OTA_RELEASE_CHANNEL_LADDER,
  channelBadgeTone,
  channelTableBadgeLabel,
} from './data/channelLabels';
import {
  enrichBuildsWithReleaseChannels,
  siblingSigningBuild,
  type BuildWithReleaseChannels,
} from './data/buildReleaseChannels';
import {
  defaultPromoteTarget,
  describePromoteOption,
  getPromoteTargetOptions,
  hasPromoteTarget,
  type PromoteTargetOption,
} from './data/promoteStateMachine';
import {
  signingKeyBadgeTone,
  signingKeyDescription,
  signingKeyLabel,
  signingKeyShortLabel,
} from './data/signingLabels';
import type { UserRole } from './data/permissions';
import { useAuth } from '../../../providers/AuthProvider';

type AppFilter = OtaAppId | 'all';
type ReleaseChannelFilter = OtaReleaseChannelId | 'all';

type SortKey =
  | 'appDir'
  | 'versionCode'
  | 'versionName'
  | 'sha256'
  | 'uploadedAt'
  | 'releaseChannels'
  | 'packageId'
  | 'sizeBytes';

type SortDir = 'asc' | 'desc';

interface FilterState {
  app: AppFilter;
  releaseChannel: ReleaseChannelFilter;
  q: string;
}

interface SortState {
  key: SortKey;
  dir: SortDir;
}

function compareBuilds(a: BuildWithReleaseChannels, b: BuildWithReleaseChannels, sort: SortState): number {
  let cmp = 0;
  switch (sort.key) {
    case 'appDir':
      cmp = a.appDir.localeCompare(b.appDir);
      break;
    case 'versionCode':
      cmp = a.versionCode - b.versionCode;
      break;
    case 'versionName':
      cmp = a.versionName.localeCompare(b.versionName);
      break;
    case 'sha256':
      cmp = a.sha256.localeCompare(b.sha256);
      break;
    case 'uploadedAt':
      cmp = new Date(a.uploadedAt).getTime() - new Date(b.uploadedAt).getTime();
      break;
    case 'packageId':
      cmp = a.packageId.localeCompare(b.packageId);
      break;
    case 'sizeBytes':
      cmp = a.sizeBytes - b.sizeBytes;
      break;
    case 'releaseChannels': {
      cmp = a.activeReleaseChannels.length - b.activeReleaseChannels.length;
      if (cmp === 0) {
        const order = (ch: OtaReleaseChannelId | undefined) =>
          ch ? OTA_RELEASE_CHANNEL_ORDER.indexOf(ch) : 99;
        cmp =
          order(a.activeReleaseChannels[0]) - order(b.activeReleaseChannels[0]);
      }
      break;
    }
  }
  return sort.dir === 'asc' ? cmp : -cmp;
}

export function OtaBuildsPage() {
  const { user } = useAuth();
  const role = (user?.role ?? 'admin') as UserRole;
  const { data: apps = [] } = useApps();
  const { data: builds = [] } = useBuilds();
  const { data: pointers = [] } = usePointers();

  const [filter, setFilter] = useState<FilterState>({
    app: 'all',
    releaseChannel: 'all',
    q: '',
  });
  const [sort, setSort] = useState<SortState>({ key: 'uploadedAt', dir: 'desc' });
  const [expanded, setExpanded] = useState<string | null>(null);
  const [promoteTarget, setPromoteTarget] = useState<BuildWithReleaseChannels | null>(null);
  const signingPromoteMutation = usePromoteBuild();
  const releasePromoteMutation = usePromoteToReleaseChannel();
  const isPromoting = signingPromoteMutation.isPending || releasePromoteMutation.isPending;

  const enriched = useMemo(
    () => enrichBuildsWithReleaseChannels(builds, pointers),
    [builds, pointers],
  );

  const filtered = useMemo(() => {
    const rows = enriched.filter((b) => {
      if (filter.app !== 'all' && b.appDir !== filter.app) return false;
      if (
        filter.releaseChannel !== 'all' &&
        !b.activeReleaseChannels.includes(filter.releaseChannel)
      ) {
        return false;
      }
      if (filter.q) {
        const q = filter.q.toLowerCase();
        if (
          !b.sha256.toLowerCase().includes(q) &&
          !b.versionName.toLowerCase().includes(q) &&
          !String(b.versionCode).includes(q)
        ) {
          return false;
        }
      }
      return true;
    });
    return [...rows].sort((a, b) => compareBuilds(a, b, sort));
  }, [enriched, filter, sort]);

  const toggleSort = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'uploadedAt' || key === 'versionCode' ? 'desc' : 'asc' },
    );
  };

  return (
    <div className="px-8 py-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-400">
            Build Registry
          </div>
          <h1 className="text-2xl font-bold text-white mt-1">Builds</h1>
          <p className="text-sm text-slate-400 mt-1">
            Every immutable APK in R2. Signing key is how the APK was signed; release channel badges
            show where that version code is live for devices.
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Total</div>
          <div className="text-2xl font-bold text-white font-mono">{builds.length}</div>
        </div>
      </div>

      <Card padded={false} className="px-4 py-3 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 p-1 rounded-lg bg-slate-900/60 border border-slate-700">
            <FilterChip
              active={filter.app === 'all'}
              onClick={() => setFilter((f) => ({ ...f, app: 'all' }))}
            >
              All apps
            </FilterChip>
            {apps.map((a) => (
              <FilterChip
                key={a.id}
                active={filter.app === a.id}
                onClick={() => setFilter((f) => ({ ...f, app: a.id }))}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full inline-block mr-1.5"
                  style={{ background: a.color }}
                />
                {a.id}
              </FilterChip>
            ))}
          </div>
          <div className="flex-1" />
          <div className="relative">
            <MagnifyingGlassIcon className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              placeholder="sha256, versionName, versionCode…"
              value={filter.q}
              onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
              className="pl-8 pr-3 py-1.5 w-64 bg-slate-900/60 border border-slate-700 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Release channel
          </span>
          <div className="flex items-center gap-1 p-1 rounded-lg bg-slate-900/60 border border-slate-700 flex-wrap">
            <FilterChip
              active={filter.releaseChannel === 'all'}
              onClick={() => setFilter((f) => ({ ...f, releaseChannel: 'all' }))}
            >
              All
            </FilterChip>
            {OTA_RELEASE_CHANNEL_ORDER.map((ch) => (
              <FilterChip
                key={ch}
                active={filter.releaseChannel === ch}
                onClick={() => setFilter((f) => ({ ...f, releaseChannel: ch }))}
              >
                {channelTableBadgeLabel(ch)}
              </FilterChip>
            ))}
          </div>
        </div>
      </Card>

      <Card padded={false} className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/60 sticky top-0">
              <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                <SortableTh label="App" sortKey="appDir" sort={sort} onSort={toggleSort} className="w-14" />
                <SortableTh
                  label="Build"
                  title="Incrementing android package build number"
                  sortKey="versionCode"
                  sort={sort}
                  onSort={toggleSort}
                  className="w-14"
                />
                <SortableTh
                  label="🏷️"
                  title="versionName — semantic version tag chosen by releasing team member"
                  sortKey="versionName"
                  sort={sort}
                  onSort={toggleSort}
                  className="w-10"
                />
                <SortableTh label="sha256" sortKey="sha256" sort={sort} onSort={toggleSort} className="w-24" />
                <SortableTh label="Uploaded" sortKey="uploadedAt" sort={sort} onSort={toggleSort} className="w-28" />
                <SortableTh label="Key" sortKey="appDir" sort={sort} onSort={toggleSort} className="w-20" />
                <SortableTh
                  label="Channels"
                  sortKey="releaseChannels"
                  sort={sort}
                  onSort={toggleSort}
                  className="min-w-[100px]"
                />
                <th className="text-left px-2 py-3 w-28 whitespace-nowrap">Status</th>
                <SortableTh
                  label="Size"
                  sortKey="sizeBytes"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                  className="w-16"
                />
                <SortableTh label="packageId" sortKey="packageId" sort={sort} onSort={toggleSort} className="min-w-0" />
                <th className="w-9 py-3" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => {
                const key = `${b.channel}-${b.appDir}-${b.versionCode}-${b.packageId}`;
                return (
                  <BuildRow
                    key={key}
                    b={b}
                    isOpen={expanded === key}
                    onToggle={() => setExpanded(expanded === key ? null : key)}
                    canPromote={hasPromoteTarget(
                      b,
                      pointers,
                      role,
                      b.activeReleaseChannels,
                    )}
                    onPromote={() => setPromoteTarget(b)}
                    locked={isPromoting}
                  />
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-5 py-12 text-center text-sm text-slate-500">
                    No builds match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {promoteTarget && (
        <PromoteDialog
          build={promoteTarget}
          allBuilds={builds}
          role={role}
          pointers={pointers}
          isPromoting={isPromoting}
          error={
            signingPromoteMutation.error?.message ??
            releasePromoteMutation.error?.message ??
            null
          }
          onCancel={() => {
            if (isPromoting) return;
            signingPromoteMutation.reset();
            releasePromoteMutation.reset();
            setPromoteTarget(null);
          }}
          onConfirm={async (targetChannel) => {
            const promoteSigning = async (b: Build) => {
              if (b.isPromoted) return;
              await signingPromoteMutation.mutateAsync({
                channel: b.channel,
                appDir: b.appDir,
                versionDir: b.versionDir,
                packageId: b.packageId,
              });
            };

            try {
              const sibling = siblingSigningBuild(promoteTarget, builds);
              await promoteSigning(promoteTarget);
              if (sibling) await promoteSigning(sibling);
              await releasePromoteMutation.mutateAsync({ targetChannel, build: promoteTarget });
              signingPromoteMutation.reset();
              releasePromoteMutation.reset();
              setPromoteTarget(null);
            } catch {
              // errors surfaced via mutation state
            }
          }}
        />
      )}
    </div>
  );
}

function SortableTh({
  label,
  title,
  sortKey,
  sort,
  onSort,
  align = 'left',
  className,
}: {
  label: string;
  /** Native tooltip on header (column help). */
  title?: string;
  sortKey: SortKey;
  sort: SortState;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
  className?: string;
}) {
  const active = sort.key === sortKey;
  const pad = align === 'right' ? 'text-right px-2 py-3' : 'text-left px-2 py-3';
  return (
    <th className={cx(pad, sortKey === 'appDir' && 'pl-5', className)}>
      <button
        type="button"
        title={title}
        onClick={() => onSort(sortKey)}
        className={cx(
          'inline-flex items-center gap-1 hover:text-slate-300 transition-colors',
          active ? 'text-cyan-400' : 'text-slate-500',
        )}
      >
        {label}
        {active ? (
          sort.dir === 'asc' ? (
            <ChevronUpIcon className="w-3 h-3" />
          ) : (
            <ChevronDownIcon className="w-3 h-3" />
          )
        ) : (
          <span className="w-3 h-3 inline-block opacity-30">
            <ChevronUpIcon className="w-3 h-3" />
          </span>
        )}
      </button>
    </th>
  );
}

function ReleaseChannelBadges({ channels }: { channels: OtaReleaseChannelId[] }) {
  if (channels.length === 0) {
    return <span className="text-xs text-slate-500">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1 max-w-[220px]">
      {channels.map((ch) => (
        <Badge key={ch} tone={channelBadgeTone(ch)}>
          {channelTableBadgeLabel(ch)}
        </Badge>
      ))}
    </div>
  );
}

function PromoteDialog({
  build,
  allBuilds,
  role,
  pointers,
  isPromoting,
  error,
  onCancel,
  onConfirm,
}: {
  build: BuildWithReleaseChannels;
  allBuilds: Build[];
  role: UserRole;
  pointers: ChannelPointer[];
  isPromoting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (targetChannel: OtaReleaseChannelId) => void;
}) {
  const options = useMemo(
    () => getPromoteTargetOptions(build, pointers, role, build.activeReleaseChannels),
    [build, pointers, role],
  );
  const [targetChannel, setTargetChannel] = useState<OtaReleaseChannelId | null>(() =>
    defaultPromoteTarget(options),
  );

  const selected = options.find((o) => o.channel === targetChannel);
  const sibling = siblingSigningBuild(build, allBuilds);
  const canConfirm = selected?.enabled === true;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="w-[1100px] max-w-[95vw] max-h-[92vh] flex flex-col bg-slate-900 border border-slate-700 rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <RocketLaunchIcon className="w-5 h-5 text-cyan-400" />
            <h2 className="text-base font-bold text-white">Promote build</h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={isPromoting}
            className="text-slate-400 hover:text-white disabled:opacity-50"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="px-5 py-5 grid grid-cols-[1fr_1fr] gap-5 text-sm text-slate-300 min-h-0 flex-1 overflow-hidden">
          <div className="flex flex-col min-h-0 space-y-4 overflow-hidden">
            <p>
              Choose a <span className="text-white font-semibold">release channel</span> (device
              rollout). This APK uses the <SigningChip signingKey={build.signingKey} /> —{' '}
              {signingKeyDescription(build.signingKey)}
            </p>
            <p className="text-xs text-slate-500">
              Ladder: {OTA_RELEASE_CHANNEL_LADDER.map(channelTableBadgeLabel).join(' → ')}. Both
              signing keys normally advance together on the same channel.
              {sibling && (
                <>
                  {' '}
                  A matching <SigningChip signingKey={sibling.signingKey} /> build will be promoted
                  in parallel.
                </>
              )}
            </p>

            <div className="space-y-2">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                Target release channel
              </div>
              <div className="space-y-2">
                {options.map((option) => (
                  <PromoteChannelOption
                    key={option.channel}
                    option={option}
                    selected={targetChannel === option.channel}
                    disabled={isPromoting}
                    onSelect={() => setTargetChannel(option.channel)}
                  />
                ))}
              </div>
            </div>

            {selected?.enabled && (
              <p className="text-xs text-slate-400 border border-slate-700/60 rounded-lg px-3 py-2 bg-slate-800/40">
                {describePromoteOption(selected)}
                {(!build.isPromoted || (sibling && !sibling.isPromoted)) && (
                  <>
                    {' '}
                    R2 signing manifests will be updated so devices can fetch the APK(s).
                  </>
                )}
              </p>
            )}

            <div className="bg-slate-800/60 border border-slate-700 rounded-lg divide-y divide-slate-700/60">
              <DialogKV label="Signing key">
                <SigningChip signingKey={build.signingKey} />
              </DialogKV>
              {sibling && (
                <DialogKV label="Paired build">
                  <SigningChip signingKey={sibling.signingKey} />{' '}
                  <span className="text-slate-500 font-mono text-[11px]">same vc</span>
                </DialogKV>
              )}
              <DialogKV label="Package ID" mono>{build.packageId}</DialogKV>
              <DialogKV label="Short name" mono>{build.shortName}</DialogKV>
              <DialogKV label="App dir" mono>{build.appDir}</DialogKV>
              <DialogKV label="Build number" mono title="Incrementing android package build number">
                {build.versionCode}
              </DialogKV>
              <DialogKV
                label="Build name"
                mono
                title="versionName — semantic version tag chosen by releasing team member"
              >
                {build.versionName}
              </DialogKV>
              <DialogKV label="R2 version dir" mono title="Storage folder segment in R2 (often matches build number)">
                {build.versionDir}
              </DialogKV>
              <DialogKV label="Size" mono>
                {(build.sizeBytes / (1024 * 1024)).toFixed(2)} MB
                <span className="text-slate-500"> ({build.sizeBytes.toLocaleString()} bytes)</span>
              </DialogKV>
              <DialogKV label="SHA-256" mono>
                <span className="text-cyan-400 break-all">{build.sha256}</span>
              </DialogKV>
              <DialogKV label="APK key" mono>
                <span className="break-all">{build.apkKey}</span>
              </DialogKV>
              <DialogKV label="Manifest key" mono>
                <span className="break-all">{build.manifestKey}</span>
              </DialogKV>
              <DialogKV label="Uploaded" mono>
                {new Date(build.uploadedAt).toLocaleString('en-NZ')}{' '}
                <span className="text-slate-500">
                  ({formatDistanceToNow(new Date(build.uploadedAt), { addSuffix: true })})
                </span>
              </DialogKV>
              <DialogKV label="R2 manifest">
                {build.isPromoted && (!sibling || sibling.isPromoted) ? (
                  <Badge tone="success">PROMOTED</Badge>
                ) : (
                  <span className="text-slate-500">
                    {build.isPromoted ? 'this key only' : 'will promote on confirm'}
                  </span>
                )}
              </DialogKV>
            </div>

            {error && (
              <div className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2">
                {error}
              </div>
            )}
          </div>

          <div className="flex flex-col min-h-0">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">
              Release notes
            </div>
            <div className="flex-1 min-h-0 bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-200 whitespace-pre-wrap overflow-y-auto">
              {build.releaseNotes?.trim() ? build.releaseNotes : (
                <span className="text-slate-500 italic">No release notes.</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-700">
          <Btn variant="secondary" onClick={onCancel} disabled={isPromoting}>
            Cancel
          </Btn>
          <Btn
            variant="primary"
            onClick={() => targetChannel && onConfirm(targetChannel)}
            disabled={isPromoting || !canConfirm || !targetChannel}
            leftIcon={<RocketLaunchIcon className="w-4 h-4" />}
          >
            {isPromoting ? 'Promoting…' : 'Promote to channel'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function PromoteChannelOption({
  option,
  selected,
  disabled,
  onSelect,
}: {
  option: PromoteTargetOption;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const id = `promote-${option.channel}`;
  return (
    <label
      htmlFor={id}
      className={cx(
        'flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors',
        !option.enabled && 'opacity-50 cursor-not-allowed',
        selected && option.enabled
          ? 'border-cyan-500/50 bg-cyan-500/10'
          : 'border-slate-700 bg-slate-800/40 hover:border-slate-600',
      )}
    >
      <input
        id={id}
        type="radio"
        name="promote-channel"
        className="mt-1 text-cyan-500 border-slate-600 bg-slate-900"
        checked={selected}
        disabled={disabled || !option.enabled}
        onChange={onSelect}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge tone={channelBadgeTone(option.channel)}>{channelTableBadgeLabel(option.channel)}</Badge>
          <span className="text-[10px] uppercase tracking-wider text-slate-500">{option.kind}</span>
        </div>
        <div className="text-xs text-slate-400 mt-1">
          {option.enabled
            ? describePromoteOption(option)
            : (option.blockedReason ?? 'Unavailable')}
        </div>
      </div>
    </label>
  );
}

function SigningChip({ signingKey }: { signingKey: Build['signingKey'] }) {
  const tone = signingKeyBadgeTone(signingKey);
  const styles =
    tone === 'warning'
      ? 'bg-amber-500/15 text-amber-300 border-amber-500/40 shadow-sm shadow-amber-500/10'
      : 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40 shadow-sm shadow-cyan-500/10';
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border font-mono text-[11px] font-bold uppercase tracking-wider',
        styles,
      )}
    >
      <span
        className={cx(
          'w-1.5 h-1.5 rounded-full',
          tone === 'warning' ? 'bg-amber-400' : 'bg-cyan-400',
        )}
      />
      {signingKeyLabel(signingKey)}
    </span>
  );
}

function DialogKV({
  label,
  children,
  mono,
  title,
}: {
  label: string;
  children: ReactNode;
  mono?: boolean;
  title?: string;
}) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 px-3 py-2 items-start" title={title}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 pt-0.5">
        {label}
      </div>
      <div className={cx('text-xs text-slate-200', mono && 'font-mono')}>{children}</div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'px-2.5 py-1 rounded-md text-xs font-semibold flex items-center',
        active ? 'bg-cyan-500/20 text-cyan-300' : 'text-slate-400 hover:text-white',
      )}
    >
      {children}
    </button>
  );
}

interface BuildRowProps {
  b: BuildWithReleaseChannels;
  isOpen: boolean;
  onToggle: () => void;
  canPromote: boolean;
  onPromote: () => void;
  locked: boolean;
}

function BuildRow({ b, isOpen, onToggle, canPromote, onPromote, locked }: BuildRowProps) {
  const shaShort = b.sha256.slice(0, 10);
  const sizeMb = (b.sizeBytes / (1024 * 1024)).toFixed(2);
  const liveOnChannel = b.activeReleaseChannels.length > 0;

  return (
    <Fragment>
      <tr
        className={cx(
          'group border-t border-slate-700/40 hover:bg-slate-700/20 cursor-pointer density-row',
          isOpen && 'bg-slate-800/40',
        )}
        onClick={onToggle}
      >
        <td className="px-3 py-2">
          <AppBadge app={b.appDir as OtaAppId} />
        </td>
        <td className="px-2 py-2">
          <span className="text-white font-mono font-bold text-sm">{b.versionCode}</span>
        </td>
        <td className="px-2 py-2 font-mono text-sm text-slate-300">{b.versionName}</td>
        <td className="px-2 py-2">
          <code className="text-[10px] font-mono text-cyan-400 bg-cyan-500/10 px-1 py-0.5 rounded border border-cyan-500/20">
            {shaShort}
          </code>
        </td>
        <td className="px-2 py-2 text-[11px] text-slate-400 font-mono whitespace-nowrap">
          {formatDistanceToNow(new Date(b.uploadedAt), { addSuffix: true })}
        </td>
        <td className="px-2 py-2">
          <Badge tone={signingKeyBadgeTone(b.signingKey)}>{signingKeyShortLabel(b.signingKey)}</Badge>
        </td>
        <td className="px-2 py-2">
          <ReleaseChannelBadges channels={b.activeReleaseChannels} />
        </td>
        <td className="px-2 py-2 whitespace-nowrap">
          {!b.isValid ? (
            <span title={b.error ?? 'Invalid build manifest'}>
              <Badge tone="error">🚫 Invalid</Badge>
            </span>
          ) : liveOnChannel ? (
            <Badge tone="blue">Live</Badge>
          ) : (
            <Badge tone="neutral">Inactive</Badge>
          )}
        </td>
        <td className="px-2 py-2 text-right font-mono text-sm text-slate-300 whitespace-nowrap">
          {sizeMb} MB
        </td>
        <td
          className="px-2 py-2 text-[11px] font-mono text-slate-500 truncate max-w-[200px]"
          title={b.packageId}
        >
          {b.packageId}
        </td>
        <td className="px-1 py-2 w-9" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            disabled={!canPromote || locked || !b.isValid}
            title={
              !b.isValid
                ? (b.error ?? 'Invalid build — cannot promote')
                : locked
                  ? 'Promoting…'
                  : !canPromote
                    ? 'No valid release channel'
                    : 'Promote to release channel'
            }
            onClick={onPromote}
            className={cx(
              'p-1.5 rounded-md transition-all',
              'text-cyan-400 hover:bg-cyan-500/20 hover:text-cyan-300',
              'opacity-0 group-hover:opacity-100 focus:opacity-100',
              isOpen && 'opacity-100',
              (!canPromote || locked || !b.isValid) && 'opacity-40 cursor-not-allowed hover:bg-transparent',
            )}
            aria-label="Promote build"
          >
            <RocketLaunchIcon className="w-4 h-4" />
          </button>
        </td>
      </tr>
      {isOpen && (
        <tr className="bg-slate-900/60 border-t border-slate-700/40">
          <td colSpan={11} className="px-5 py-5">
            <div className="flex items-center justify-end gap-2 mb-4">
              <Btn
                size="sm"
                variant="primary"
                leftIcon={<RocketLaunchIcon className="w-3.5 h-3.5" />}
                disabled={!canPromote || locked}
                onClick={onPromote}
              >
                Promote…
              </Btn>
            </div>
            <div className="grid grid-cols-[1fr_360px] gap-6">
              <ReleaseNotesEditor build={b} />
              <div className="space-y-3">
                <KV
                  label="Build number"
                  value={String(b.versionCode)}
                  title="Incrementing android package build number"
                  futureLink
                />
                <KV
                  label="Build name"
                  value={b.versionName}
                  title="versionName — semantic version tag chosen by releasing team member"
                  futureLink
                />
                <KV label="Signing key" value={signingKeyLabel(b.signingKey)} />
                <KV
                  label="Live release channels"
                  value={
                    b.activeReleaseChannels.length > 0
                      ? b.activeReleaseChannels.map(channelTableBadgeLabel).join(', ')
                      : 'none'
                  }
                />
                <KV label="Artefact SHA-256" value={b.sha256} />
                <KV label="APK key" value={b.apkKey} />
                <KV label="Manifest key" value={b.manifestKey} />
              </div>
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function KV({
  label,
  value,
  title,
  link,
  futureLink,
}: {
  label: string;
  value: string;
  title?: string;
  link?: boolean;
  /** Styled for a future GitHub Actions / workflow URL. */
  futureLink?: boolean;
}) {
  return (
    <div title={title}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div
        className={cx(
          'text-[11px] font-mono mt-0.5 truncate',
          link || futureLink
            ? 'text-cyan-400'
            : 'text-slate-300',
          futureLink && 'opacity-90',
          link && 'hover:underline cursor-pointer',
        )}
      >
        {value}
      </div>
    </div>
  );
}

export default OtaBuildsPage;
